"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  setAuthContextSource,
  setUnauthorizedHandler,
  type RequestAuthContext,
} from "@/lib/api/client";
import { getCurrentUser, login, logout, refresh } from "@/lib/api/endpoints";
import type { AuthResult, LoginRequest, UserProfile } from "@/lib/api/types";
import {
  clearSession,
  isExpired,
  readSession,
  subscribeToSession,
  writeSession,
  type StoredSession,
} from "@/lib/auth/session";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  status: AuthStatus;
  /** Token pair for the current session; `null` unless authenticated. */
  session: StoredSession | null;
  profile: UserProfile | null;
  permissions: readonly string[];
  /** Company stamped onto outgoing requests as `X-Company-Id`. */
  activeCompanyId: string | null;
  signIn: (input: LoginRequest) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  setActiveCompany: (companyId: string | null) => void;
  hasPermission: (key: string) => boolean;
}

interface AuthState {
  status: AuthStatus;
  session: StoredSession | null;
  profile: UserProfile | null;
  activeCompanyId: string | null;
}

const LOADING_STATE: AuthState = {
  status: "loading",
  session: null,
  profile: null,
  activeCompanyId: null,
};

const ANONYMOUS_STATE: AuthState = {
  status: "anonymous",
  session: null,
  profile: null,
  activeCompanyId: null,
};

const NO_PERMISSIONS: readonly string[] = [];

const AuthContext = createContext<AuthContextValue | null>(null);

function sessionFromAuthResult(result: AuthResult): StoredSession {
  return {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    expiresAt: result.expiresAt,
  };
}

/**
 * `/auth/login` and `/auth/refresh` already return everything `/auth/me` would,
 * so the profile is projected from the token response instead of costing an
 * extra round trip on every sign-in.
 */
function profileFromAuthResult(result: AuthResult): UserProfile {
  return {
    user: result.user,
    permissions: result.permissions,
    roles: result.roles,
    companies: result.companies,
    tenantId: result.tenantId,
    tenantCode: result.tenantCode,
    tenantName: result.tenantName,
    currentCompanyId: result.companyId,
    currentBranchId: result.branchId,
  };
}

function pickCompanyId(profile: UserProfile): string | null {
  if (profile.currentCompanyId) return profile.currentCompanyId;
  const preferred =
    profile.companies.find((company) => company.isDefault) ?? profile.companies[0];
  return preferred?.companyId ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(LOADING_STATE);

  const mountedRef = useRef(false);
  // Mirrors of the state the API client reads. They are written synchronously so
  // a request issued in the same tick as a token change never sees a stale one.
  const tokensRef = useRef<StoredSession | null>(null);
  const profileRef = useRef<UserProfile | null>(null);
  const companyRef = useRef<string | null>(null);

  const commit = useCallback((next: AuthState) => {
    tokensRef.current = next.session;
    profileRef.current = next.profile;
    companyRef.current = next.activeCompanyId;
    if (mountedRef.current) setState(next);
  }, []);

  const applyAuthResult = useCallback(
    (result: AuthResult) => {
      const session = sessionFromAuthResult(result);
      const profile = profileFromAuthResult(result);
      writeSession(session);
      commit({
        status: "authenticated",
        session,
        profile,
        activeCompanyId: pickCompanyId(profile),
      });
    },
    [commit],
  );

  /** Drops the session locally without calling the server. */
  const forget = useCallback(() => {
    clearSession();
    commit(ANONYMOUS_STATE);
  }, [commit]);

  const bootstrap = useCallback(async () => {
    const stored = readSession();
    if (!stored) {
      commit(ANONYMOUS_STATE);
      return;
    }

    // Visible to the API client before the first request goes out.
    tokensRef.current = stored;

    if (isExpired(stored)) {
      try {
        applyAuthResult(await refresh({ refreshToken: stored.refreshToken }));
      } catch {
        forget();
      }
      return;
    }

    try {
      const profile = await getCurrentUser();
      // A mid-flight 401 recovery may have rotated the pair under us.
      const session = tokensRef.current;
      if (!session) {
        commit(ANONYMOUS_STATE);
        return;
      }
      commit({
        status: "authenticated",
        session,
        profile,
        activeCompanyId: pickCompanyId(profile),
      });
    } catch {
      // Tokens are deliberately left on disk: an outage should not force the
      // user to retype credentials once the network comes back.
      commit(ANONYMOUS_STATE);
    }
  }, [applyAuthResult, commit, forget]);

  const readAuthContext = useCallback((): RequestAuthContext | null => {
    const tokens = tokensRef.current;
    if (!tokens) return null;

    const companyId = companyRef.current;
    const branchId = companyId
      ? (profileRef.current?.companies.find(
          (company) => company.companyId === companyId,
        )?.branchId ?? null)
      : null;

    return { accessToken: tokens.accessToken, companyId, branchId };
  }, []);

  const handleUnauthorized = useCallback(async (): Promise<boolean> => {
    const tokens = tokensRef.current;
    if (!tokens) return false;

    try {
      applyAuthResult(await refresh({ refreshToken: tokens.refreshToken }));
      return true;
    } catch {
      forget();
      return false;
    }
  }, [applyAuthResult, forget]);

  // Declared first so the flag is already true when the effects below settle.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Registered before bootstrap runs, so the very first request is authenticated
  // and can recover from a 401.
  useEffect(() => {
    setAuthContextSource(readAuthContext);
    setUnauthorizedHandler(handleUnauthorized);
    return () => {
      setAuthContextSource(null);
      setUnauthorizedHandler(null);
    };
  }, [handleUnauthorized, readAuthContext]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // An ERP is routinely open in several tabs; none of them may keep serving a
  // session that was ended somewhere else.
  useEffect(
    () =>
      subscribeToSession((stored) => {
        if (stored?.accessToken === tokensRef.current?.accessToken) return;
        if (!stored) {
          forget();
          return;
        }
        void bootstrap();
      }),
    [bootstrap, forget],
  );

  const signIn = useCallback(
    async (input: LoginRequest): Promise<AuthResult> => {
      const tenantCode = input.tenantCode?.trim();
      const result = await login({
        tenantCode: tenantCode ? tenantCode : null,
        userNameOrEmail: input.userNameOrEmail.trim(),
        password: input.password,
      });
      applyAuthResult(result);
      return result;
    },
    [applyAuthResult],
  );

  const signOut = useCallback(async () => {
    const tokens = tokensRef.current;
    // Local state is cleared first: a failing revocation call must never leave
    // the user looking signed in.
    forget();
    if (!tokens) return;

    try {
      await logout(
        { refreshToken: tokens.refreshToken },
        { auth: { accessToken: tokens.accessToken } },
      );
    } catch {
      // Server-side revocation is best effort; the token expires on its own.
    }
  }, [forget]);

  const setActiveCompany = useCallback((companyId: string | null) => {
    const companies = profileRef.current?.companies ?? [];
    if (companyId && !companies.some((company) => company.companyId === companyId)) {
      return;
    }

    companyRef.current = companyId;
    if (!mountedRef.current) return;
    setState((previous) =>
      previous.activeCompanyId === companyId
        ? previous
        : { ...previous, activeCompanyId: companyId },
    );
  }, []);

  const permissions = state.profile?.permissions ?? NO_PERMISSIONS;
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  // `hr.*` asks whether ANY permission of that family is held. It is how a whole module is gated:
  // when the platform switches a module off, even an administrator's list carries none of its keys.
  const hasPermission = useCallback(
    (key: string) => {
      if (key.length === 0) return false;
      if (!key.endsWith(".*")) return permissionSet.has(key);
      const prefix = key.slice(0, -1);
      return permissions.some((held) => held.startsWith(prefix));
    },
    [permissionSet, permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status: state.status,
      session: state.session,
      profile: state.profile,
      permissions,
      activeCompanyId: state.activeCompanyId,
      signIn,
      signOut,
      setActiveCompany,
      hasPermission,
    }),
    [
      hasPermission,
      permissions,
      setActiveCompany,
      signIn,
      signOut,
      state.activeCompanyId,
      state.profile,
      state.session,
      state.status,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used within an <AuthProvider>.");
  }
  return value;
}

/**
 * Mounts an `AuthProvider` only when the tree does not already have one.
 *
 * Two nested providers would each bootstrap the session and each register the
 * module-level API client hooks, so route-group layouts use this instead of
 * `AuthProvider` directly and stay correct whether or not the root layout
 * already provides auth.
 */
export function EnsureAuthProvider({ children }: { children: ReactNode }) {
  const existing = useContext(AuthContext);
  if (existing) return <>{children}</>;
  return <AuthProvider>{children}</AuthProvider>;
}
